import {
  ARTIFACT_COMBINE_ROUTE,
  ARTIFACTS_ROUTE,
  type ArtifactCombineResponse,
  type ArtifactsResponse,
  artifactCombineResponseSchema,
  artifactsResponseSchema,
  CARD_COMBINE_ROUTE,
  CARD_EQUIP_ROUTE,
  CARD_UNEQUIP_ROUTE,
  CARDS_ROUTE,
  type CardCombineResponse,
  type CardMutationResponse,
  type CardSlot,
  type CardsListResponse,
  cardCombineResponseSchema,
  cardMutationResponseSchema,
  cardsListResponseSchema,
  ECONOMY_ME_ROUTE,
  type EconomyMeResponse,
  EQUIPMENT_EQUIP_ROUTE,
  EQUIPMENT_ROUTE,
  EQUIPMENT_UNEQUIP_ROUTE,
  EQUIPMENT_UPGRADE_PREVIEW_ROUTE,
  EQUIPMENT_UPGRADE_ROUTE,
  type EquipmentComponent,
  type EquipmentListResponse,
  type EquipmentMutationResponse,
  type EquipmentUpgradePreviewResponse,
  type EquipmentUpgradeResponse,
  economyMeResponseSchema,
  equipmentListResponseSchema,
  equipmentMutationResponseSchema,
  equipmentUpgradePreviewResponseSchema,
  equipmentUpgradeResponseSchema,
  GACHA_BANNER_ROUTE,
  GACHA_HISTORY_ROUTE,
  GACHA_PULL_ROUTE,
  type GachaBannerResponse,
  type GachaHistoryResponse,
  type GachaPullResponse,
  gachaBannerResponseSchema,
  gachaHistoryResponseSchema,
  gachaPullResponseSchema,
  HUNT_BLEED_DETONATE_ROUTE,
  HUNT_BOOTSTRAP_ROUTE,
  HUNT_CARD_ACTIVATE_ROUTE,
  HUNT_CARD_RELEASE_ROUTE,
  HUNT_CHECKPOINT_ROUTE,
  HUNT_RESTART_ROUTE,
  HUNT_TRAVEL_ROUTE,
  type HuntBleedDetonateResponse,
  type HuntBootstrapResponse,
  type HuntCardActivateResponse,
  type HuntCardReleaseResponse,
  type HuntCheckpointResponse,
  huntBleedDetonateResponseSchema,
  huntBootstrapResponseSchema,
  huntCardActivateResponseSchema,
  huntCardReleaseResponseSchema,
  huntCheckpointResponseSchema,
  INVENTORY_ROUTE,
  type InventoryResponse,
  inventoryResponseSchema,
  JEWEL_PRIMORDIAL_INCORPORATE_ROUTE,
  JEWEL_SOCKET_INSERT_ROUTE,
  JEWEL_SOCKET_REMOVE_ROUTE,
  JEWEL_SOCKET_SWAP_ROUTE,
  JEWELS_ROUTE,
  type JewelMutationResponse,
  type JewelsResponse,
  jewelMutationResponseSchema,
  jewelsResponseSchema,
  LOADOUT_EQUIP_ROUTE,
  type LoadoutEquipResponse,
  loadoutEquipResponseSchema,
  MAINTENANCE_REASON,
  MAP_LIST_ROUTE,
  MARKET_HISTORY_ROUTE,
  MARKET_LISTINGS_ROUTE,
  MARKET_MINE_ROUTE,
  MARKET_QUOTE_ROUTE,
  type MapListResponse,
  type MarketBrowseResponse,
  type MarketCategory,
  type MarketHistoryResponse,
  type MarketListingResponse,
  type MarketQuoteResponse,
  type MarketSort,
  MINING_ASSIGN_ROUTE,
  MINING_CANCEL_ROUTE,
  MINING_COLLECT_ROUTE,
  MINING_REMOVE_ROUTE,
  MINING_STATE_ROUTE,
  type MiningCollectResponse,
  type MiningState,
  mapListResponseSchema,
  marketBrowseResponseSchema,
  marketHistoryResponseSchema,
  marketListingResponseSchema,
  marketListingRoute,
  marketQuoteResponseSchema,
  miningCollectResponseSchema,
  miningStateResponseSchema,
  PET_ACTIVATE_ROUTE,
  PET_APPEARANCE_ROUTE,
  PET_EVOLVE_ROUTE,
  PET_FEED_ROUTE,
  PET_PALETTE_ROUTE,
  PET_UNLOCK_ROUTE,
  PETS_ROUTE,
  type PetActivateResponse,
  type PetAppearanceResponse,
  type PetEvolveResponse,
  type PetFeedResponse,
  type PetPaletteResponse,
  type PetsResponse,
  type PetUnlockResponse,
  PLAYER_STATE_ROUTE,
  type PlayerStateResponse,
  PROGRESSION_ALLOCATE_ROUTE,
  type ProgressionAllocateResponse,
  petActivateResponseSchema,
  petAppearanceResponseSchema,
  petEvolveResponseSchema,
  petFeedResponseSchema,
  petPaletteResponseSchema,
  petsResponseSchema,
  petUnlockResponseSchema,
  playerStateResponseSchema,
  progressionAllocateResponseSchema,
  SKIN_EQUIP_ROUTE,
  SKIN_GACHA_BANNER_ROUTE,
  SKIN_GACHA_ROLL_ROUTE,
  SKIN_UNEQUIP_ROUTE,
  SKINS_ROUTE,
  type SkinGachaBannerResponse,
  type SkinGachaRollResponse,
  type SkinMutationResponse,
  type SkinsResponse,
  STAGE_MISMATCH_REASON,
  STORE_CATALOG_ROUTE,
  STORE_IDEMPOTENCY_HEADER,
  STORE_ORDERS_ROUTE,
  STORE_RECREDIT_PREVIEW_ROUTE,
  type StoreCatalogResponse,
  type StoreOrderResponse,
  type StoreOrdersResponse,
  type StoreRecreditPreviewResponse,
  skinGachaBannerResponseSchema,
  skinGachaRollResponseSchema,
  skinMutationResponseSchema,
  skinsResponseSchema,
  storeCatalogResponseSchema,
  storeOrderResponseSchema,
  storeOrderRoute,
  storeOrdersResponseSchema,
  storeRecreditPreviewResponseSchema,
  TRADE_INVITATIONS_ROUTE,
  TRADES_INBOX_ROUTE,
  TRADES_PLAYER_SEARCH_ROUTE,
  type TradeInboxResponse,
  type TradePlayerSearchResponse,
  type TradeResponse,
  tradeInboxResponseSchema,
  tradePlayerSearchResponseSchema,
  tradeResponseSchema,
  tradeRoute,
  type UpgradeCurrency,
} from '@kenocyber/shared';
import { API_BASE_URL } from '../auth/client';
import { reportServiceOutage } from '../service/availability';

/**
 * Typed client for the authoritative hunt API.
 *
 * Network access lives HERE, never in the Phaser scene: the renderer draws a
 * simulation, it does not talk to a server or a database. Every call carries
 * the session cookie (`credentials: 'include'`) and every response is parsed
 * with the shared contract, so a drift fails here instead of corrupting the
 * HUD.
 */

/** Distinguishes "the server said no" from "the server was not reachable". */
export class HuntApiError extends Error {
  readonly status: number;
  /** Stable machine reason from the API, when it sent one. */
  readonly reason: string | null;
  /** Server-computed wait before repeating the SAME request. */
  readonly retryAfterMs: number | null;

  constructor(status: number, reason: string | null, retryAfterMs: number | null) {
    super(`hunt api error ${status}`);
    this.name = 'HuntApiError';
    this.status = status;
    this.reason = reason;
    this.retryAfterMs = retryAfterMs;
  }

  /**
   * The window is not over yet. Not an error to show a player: the client
   * waits `retryAfterMs` and repeats the same sequence and key.
   */
  get isNotDue(): boolean {
    return this.reason === 'checkpoint_not_due';
  }

  /** A conflict means another tab, a stale sequence or an expired session. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * The game is between release stages. Not an outage: it has a screen.
   *
   * Its own predicate rather than a bare 503, because the two want opposite
   * behaviour - a maintenance answer must stop the client, and an outage must
   * let it retry.
   */
  get isMaintenance(): boolean {
    return this.status === 503 && this.reason === MAINTENANCE_REASON;
  }

  /**
   * The deployment and the database disagree about which version is live.
   *
   * Distinct from maintenance because it means something different to whoever
   * reads it - nobody chose it and no player action ends it - and identical in
   * one respect that matters here: the client must stop, not retry.
   */
  get isStageMismatch(): boolean {
    return this.status === 503 && this.reason === STAGE_MISMATCH_REASON;
  }

  /** Either of the two the server shuts the game with. Both mean: stop. */
  get isServiceClosed(): boolean {
    return this.isMaintenance || this.isStageMismatch;
  }
}

async function request<T>(
  path: string,
  parse: (value: unknown) => T,
  init?: {
    method?: 'GET' | 'POST' | 'PUT';
    body?: unknown;
    /**
     * Extra request headers.
     *
     * The store's idempotency key travels in one rather than in the body: it
     * describes the REQUEST, not the purchase, and keeping it out of the body
     * is what lets that body stay a single `productId` under `.strict()`.
     */
    headers?: Record<string, string>;
  },
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: init?.method ?? 'GET',
    // The httpOnly session cookie is the only credential; nothing is stored
    // in JavaScript-readable space.
    credentials: 'include',
    headers: {
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
    ...(init?.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { message?: string; retryAfterMs?: number };
    } | null;
    const failure = new HuntApiError(
      response.status,
      body?.error?.message ?? null,
      body?.error?.retryAfterMs ?? null,
    );
    if (failure.isServiceClosed) {
      /*
       * Reported HERE, in the one function every authoritative request goes
       * through.
       *
       * Not in the hunt's error handling, not in the store's, not in each panel
       * that happens to remember: a route added next month is covered the day
       * it is written, and the one somebody forgets is the one that would leave
       * an arena running through a reset.
       *
       * It is synchronous, so the client stops in the same tick the refusal
       * arrived rather than after a `/health` round trip.
       */
      reportServiceOutage(failure.isMaintenance ? 'maintenance' : 'stage_mismatch');
    }
    throw failure;
  }
  return parse(await response.json());
}

export function fetchPlayerState(): Promise<PlayerStateResponse> {
  return request(PLAYER_STATE_ROUTE, (value) => playerStateResponseSchema.parse(value));
}

export function bootstrapHunt(options: { debug: boolean }): Promise<HuntBootstrapResponse> {
  return request(HUNT_BOOTSTRAP_ROUTE, (value) => huntBootstrapResponseSchema.parse(value), {
    method: 'POST',
    // A debug session is declared, not hidden: the server marks it
    // non-economic and refuses to credit it.
    body: options.debug ? { debug: true } : {},
  });
}

export function sendCheckpoint(input: {
  sessionId: string;
  sequence: number;
  /** Simulated time covered by THIS window; never an absolute tick. */
  clientDeltaMs: number;
  /** Digest of the client's own state. Diagnostic only, never a reward. */
  clientDigest?: string;
  idempotencyKey: string;
}): Promise<HuntCheckpointResponse> {
  // Note what is NOT sent: no XP, no Gold, no kills, no elapsed time, no
  // timestamp. The server rejects those fields outright.
  return request(HUNT_CHECKPOINT_ROUTE, (value) => huntCheckpointResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

export function restartHunt(input: {
  sessionId: string;
  idempotencyKey: string;
}): Promise<HuntBootstrapResponse> {
  return request(HUNT_RESTART_ROUTE, (value) => huntBootstrapResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * The maps this account may see, and where the ship is now.
 *
 * Read-only and authoritative. The browser never works out which maps are
 * unlocked - it asks - because an unlock is a row in a table only the server
 * can see, and a client that computed the answer would be a client that could
 * be wrong about it.
 */
export function fetchMapList(): Promise<MapListResponse> {
  return request(MAP_LIST_ROUTE, (value) => mapListResponseSchema.parse(value));
}

/**
 * Travel.
 *
 * The body carries a destination and a replay key, and the schema is
 * `.strict()` on the server, so nothing else can be smuggled in. Note what is
 * absent: no account, no current map, no unlock list, no session and no
 * snapshot. The server already knows all of them and would not believe this
 * side about any of it.
 *
 * The answer is a full authorised session on the destination map, in the same
 * shape a restart returns - so applying it is the code that already exists.
 */
export function travelToMap(input: {
  mapIndex: number;
  idempotencyKey: string;
}): Promise<HuntBootstrapResponse> {
  return request(HUNT_TRAVEL_ROUTE, (value) => huntBootstrapResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * The mining screen, in one read.
 *
 * State and fleet together, because that is how the server answers: a client
 * that fetched them separately could show a ship as dockable that the first
 * read had already docked.
 */
export function fetchMiningState(): Promise<MiningState> {
  return request(MINING_STATE_ROUTE, (value) => miningStateResponseSchema.parse(value));
}

/**
 * Docks a ship.
 *
 * The body names an instance and nothing else. Note what is absent: Power,
 * production, a balance, a timestamp, a number of seconds, an account. The
 * server knows every one of them, the schema is `.strict()` on both sides, and
 * this side does not even try.
 */
export function assignMiningShip(instanceId: string): Promise<MiningState> {
  return request(MINING_ASSIGN_ROUTE, (value) => miningStateResponseSchema.parse(value), {
    method: 'POST',
    body: { instanceId },
  });
}

/** Undocks a ship. The server settles the period it worked before it stops. */
export function removeMiningShip(instanceId: string): Promise<MiningState> {
  return request(MINING_REMOVE_ROUTE, (value) => miningStateResponseSchema.parse(value), {
    method: 'POST',
    body: { instanceId },
  });
}

/**
 * Collects.
 *
 * A replay key, never an amount: what is collected is whatever the operation
 * has, and a request that could name a figure could name a bigger one. The
 * SAME key is reused while one intention is being retried, so a lost answer
 * costs the player nothing.
 */
export function collectMining(idempotencyKey: string): Promise<MiningCollectResponse> {
  return request(MINING_COLLECT_ROUTE, (value) => miningCollectResponseSchema.parse(value), {
    method: 'POST',
    body: { idempotencyKey },
  });
}

/** Cancels. No body at all: there is nothing to say. */
export function cancelMining(): Promise<MiningState> {
  return request(MINING_CANCEL_ROUTE, (value) => miningStateResponseSchema.parse(value), {
    method: 'POST',
    body: {},
  });
}

export function allocatePoints(input: {
  /** Decimal strings: point balances are unbounded. */
  damage: string;
  health: string;
  attackSpeed: string;
  idempotencyKey: string;
}): Promise<ProgressionAllocateResponse> {
  return request(
    PROGRESSION_ALLOCATE_ROUTE,
    (value) => progressionAllocateResponseSchema.parse(value),
    { method: 'POST', body: input },
  );
}

/**
 * **Manda rebentar as marcas de sangramento desta corrida** (gate V28G.N).
 *
 * Um campo, e é tudo o que um cliente pode dizer: qual sessão. Não há alvo, não
 * há contagem e não há magnitude - a autoridade decide tudo isso a partir de um
 * estado que este browser não escreve.
 *
 * Sem chave de idempotência, e a ausência é uma decisão: uma detonação consome
 * TODAS as marcas, portanto uma repetição encontra zero e é um `no-op` seguro
 * que não escreve dano nenhum. Uma chave protegeria contra uma segunda detonação
 * que não pode existir.
 */
export function detonateBleed(sessionId: string): Promise<HuntBleedDetonateResponse> {
  return request(
    HUNT_BLEED_DETONATE_ROUTE,
    (value) => huntBleedDetonateResponseSchema.parse(value),
    { method: 'POST', body: { sessionId } },
  );
}

/**
 * **A ordem do jogador para ligar uma carta** (gate V28H.I).
 *
 * Três campos, e nenhum deles é um efeito: qual sessão, qual espaço, e qual
 * ordem. A duração, o arrefecimento e o instante são decididos pela autoridade
 * a partir de um estado que este browser não escreve.
 *
 * COM chave de idempotência, ao contrário da detonação, e a diferença tem uma
 * razão exata: uma detonação repetida encontra zero marcas e é um `no-op`
 * inofensivo; uma activação repetida encontraria a carta já ligada e seria
 * RECUSADA - o jogador veria "indisponível" por uma ordem que na verdade pegou.
 * O `commandId` faz a segunda tentativa ler a primeira.
 */
export function activateCard(input: {
  sessionId: string;
  slot: CardSlot;
  commandId: string;
}): Promise<HuntCardActivateResponse> {
  return request(HUNT_CARD_ACTIVATE_ROUTE, (value) => huntCardActivateResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * **A ordem do jogador para SOLTAR uma carga** (gate V28H.N).
 *
 * O mesmo corpo da de ligar, e um verbo diferente. Ela existe porque a Lança
 * Solar é carregada enquanto a tecla está premida: o instante em que o dedo sai
 * é uma decisão, e sob autoridade do servidor uma decisão que não chega lá não
 * existe.
 *
 * Uma soltura que chega tarde - a carga já se completou sozinha - é recusada com
 * `no_charge`, e isso não é um erro: o feixe saiu na mesma, cheio.
 */
export function releaseCard(input: {
  sessionId: string;
  slot: CardSlot;
  commandId: string;
}): Promise<HuntCardReleaseResponse> {
  return request(HUNT_CARD_RELEASE_ROUTE, (value) => huntCardReleaseResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

export function fetchInventory(input?: {
  cursor?: string;
  limit?: number;
}): Promise<InventoryResponse> {
  const query = new URLSearchParams();
  if (input?.cursor) {
    query.set('cursor', input.cursor);
  }
  if (input?.limit !== undefined) {
    query.set('limit', String(input.limit));
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return request(`${INVENTORY_ROUTE}${suffix}`, (value) => inventoryResponseSchema.parse(value));
}

/**
 * Equips an owned item.
 *
 * Note what the body cannot say: no slot, no rarity, no level, no bonus. The
 * server infers the slot from the item's own definition and recomputes every
 * number, so there is nothing here worth forging.
 */
export function equipItem(input: {
  itemInstanceId: string;
  /** The version the client believes it is changing; a race loses on this. */
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<LoadoutEquipResponse> {
  return request(LOADOUT_EQUIP_ROUTE, (value) => loadoutEquipResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Every artifact and what it is currently doing, derived by the server. */
export function fetchArtifacts(): Promise<ArtifactsResponse> {
  return request(ARTIFACTS_ROUTE, (value) => artifactsResponseSchema.parse(value));
}

/* -------------------------------------------------------------------------- */
/*  Equipment                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The five slots and every component the account owns.
 *
 * Every effective value in the answer was derived on the SERVER from the
 * instance's immutable roll. This module never computes one - not the value at
 * a level, not the Power, not the price, not the chance.
 */
export function fetchEquipment(): Promise<EquipmentListResponse> {
  return request(EQUIPMENT_ROUTE, (value) => equipmentListResponseSchema.parse(value));
}

/**
 * Puts a component in its slot.
 *
 * Note what the body cannot say: no slot. The server reads which of the five a
 * component belongs in from the item itself, so there is nothing here worth
 * forging.
 */
export function equipComponent(input: {
  itemInstanceId: string;
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<EquipmentMutationResponse> {
  return request(EQUIPMENT_EQUIP_ROUTE, (value) => equipmentMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Empties one slot. */
export function unequipComponent(input: {
  component: EquipmentComponent;
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<EquipmentMutationResponse> {
  return request(EQUIPMENT_UNEQUIP_ROUTE, (value) => equipmentMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * The authoritative quote for the next level.
 *
 * The client DISPLAYS this and confirms it; it never computes a price, a
 * chance or a risk of its own. Asking again is free and changes nothing.
 */
export function fetchUpgradeQuote(
  itemInstanceId: string,
): Promise<EquipmentUpgradePreviewResponse> {
  const query = new URLSearchParams({ itemInstanceId });
  return request(`${EQUIPMENT_UPGRADE_PREVIEW_ROUTE}?${query.toString()}`, (value) =>
    equipmentUpgradePreviewResponseSchema.parse(value),
  );
}

/**
 * One upgrade attempt.
 *
 * The body carries the level the client BELIEVES it is upgrading from, which is
 * what stops a confirmation dialog opened before another tab succeeded from
 * paying a level-9 price for a level-10 item. The idempotency key means an
 * impatient double click is one attempt, with one outcome.
 */
export function upgradeComponent(input: {
  itemInstanceId: string;
  expectedLevel: number;
  currency: UpgradeCurrency;
  useStabilizer: boolean;
  idempotencyKey: string;
}): Promise<EquipmentUpgradeResponse> {
  return request(EQUIPMENT_UPGRADE_ROUTE, (value) => equipmentUpgradeResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * This account's Gold, Cash and VIP state, straight from the server.
 *
 * A read and only a read: there is no companion call in this module that sets
 * a balance, buys VIP or moves currency, because there is no such route. The
 * account is the authenticated session's - nothing here names one.
 */
export function fetchEconomy(): Promise<EconomyMeResponse> {
  return request(ECONOMY_ME_ROUTE, (value) => economyMeResponseSchema.parse(value));
}

/**
 * Merges two identical copies into one of the next level.
 *
 * Note what the body cannot say: no artifact, no level, no result. The server
 * reads all three from the two rows under their locks, so there is nothing
 * here worth forging - and the idempotency key means an impatient double click
 * replays the first answer instead of spending a second pair.
 */
export function combineArtifacts(input: {
  firstInstanceId: string;
  secondInstanceId: string;
  idempotencyKey: string;
}): Promise<ArtifactCombineResponse> {
  return request(ARTIFACT_COMBINE_ROUTE, (value) => artifactCombineResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Every pet and what it is currently doing, derived by the server. */
export function fetchPets(): Promise<PetsResponse> {
  return request(PETS_ROUTE, (value) => petsResponseSchema.parse(value));
}

/**
 * Activates a pet, or deactivates the active one with `null`.
 *
 * The body cannot state an evolution, a rarity or a star count - all three are
 * read from the row under its lock. The version guard means two tabs that both
 * activate from the same view resolve to one winner rather than to a mess.
 */
export function activatePet(input: {
  petInstanceId: string | null;
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<PetActivateResponse> {
  return request(PET_ACTIVATE_ROUTE, (value) => petActivateResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * Evolves two copies of the same pet at the same evolution.
 *
 * Manual, like merging artifacts: nothing evolves duplicates on the player's
 * behalf. If the pet that evolves was active, the new form stays active.
 */
export function evolvePet(input: {
  firstInstanceId: string;
  secondInstanceId: string;
  idempotencyKey: string;
}): Promise<PetEvolveResponse> {
  return request(PET_EVOLVE_ROUTE, (value) => petEvolveResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Every colour and finish, with whether THIS account has unlocked it. */
export function fetchPetPalette(): Promise<PetPaletteResponse> {
  return request(PET_PALETTE_ROUTE, (value) => petPaletteResponseSchema.parse(value));
}

/**
 * Feeds one unit of one food to one pet.
 *
 * The body names an instance and a food id and nothing else: the percentage,
 * the counter and the quantity are all the server's to decide.
 */
export function feedPet(input: {
  petInstanceId: string;
  foodDefinitionId: string;
  idempotencyKey: string;
}): Promise<PetFeedResponse> {
  return request(PET_FEED_ROUTE, (value) => petFeedResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * Consumes an Energy Sphere or a Tech Paint Bucket.
 *
 * A variant already unlocked comes back as a normal answer with
 * `alreadyUnlocked`, and the item is NOT spent.
 */
export function unlockPetAppearance(input: {
  unlockDefinitionId: string;
  idempotencyKey: string;
}): Promise<PetUnlockResponse> {
  return request(PET_UNLOCK_ROUTE, (value) => petUnlockResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Wears an already-unlocked appearance. Free, and repeatable. */
export function applyPetAppearance(input: {
  petInstanceId: string;
  energyId: string;
  finishId: string;
  idempotencyKey: string;
}): Promise<PetAppearanceResponse> {
  return request(PET_APPEARANCE_ROUTE, (value) => petAppearanceResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * A identidade de UMA ordem, como UUID (gate V28H.I).
 *
 * Sem prefixo, ao contrário de `newIdempotencyKey`: o contrato da activação
 * pede um UUID, e um prefixo tornaria a string algo que o esquema recusa. O
 * cliente gera-a uma vez por ordem e repete-a em cada tentativa - é isso que faz
 * a repetição ler em vez de ligar outra vez.
 */
export function newCommandId(): string {
  return crypto.randomUUID();
}

/** Idempotency keys are client-generated per REQUEST and never reach the ledger. */
export function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

/* -------------------------------------------------------------------------- */
/*  Direct trade                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The trade panel's whole API surface.
 *
 * Every mutation carries the revision it was built against and an idempotency
 * key, and none of them carries an account id: the participant is the session
 * and the counterparty is a property of the trade. There is deliberately no
 * client-side prediction here — a trade moves real items and real currency, so
 * the panel draws what the server answered and nothing else.
 */

export function fetchTradeInbox(cursor?: string): Promise<TradeInboxResponse> {
  const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return request(`${TRADES_INBOX_ROUTE}${suffix}`, (value) =>
    tradeInboxResponseSchema.parse(value),
  );
}

export function searchTradePlayers(name: string): Promise<TradePlayerSearchResponse> {
  return request(`${TRADES_PLAYER_SEARCH_ROUTE}?name=${encodeURIComponent(name)}`, (value) =>
    tradePlayerSearchResponseSchema.parse(value),
  );
}

export function fetchTrade(tradeId: string): Promise<TradeResponse> {
  return request(tradeRoute(tradeId), (value) => tradeResponseSchema.parse(value));
}

export function inviteToTrade(counterpartyId: string): Promise<TradeResponse> {
  return request(TRADE_INVITATIONS_ROUTE, (value) => tradeResponseSchema.parse(value), {
    method: 'POST',
    body: { counterpartyId, idempotencyKey: newIdempotencyKey('trade') },
  });
}

export function respondToTrade(tradeId: string, accept: boolean): Promise<TradeResponse> {
  return request(tradeRoute(tradeId, '/respond'), (value) => tradeResponseSchema.parse(value), {
    method: 'POST',
    body: { accept, idempotencyKey: newIdempotencyKey('trade') },
  });
}

export function setTradeOffer(input: {
  tradeId: string;
  expectedRevision: number;
  items: { kind: 'instance'; instanceId: string }[];
  gold: string;
  cash: string;
}): Promise<TradeResponse> {
  return request(tradeRoute(input.tradeId, '/offer'), (value) => tradeResponseSchema.parse(value), {
    method: 'PUT',
    body: {
      expectedRevision: input.expectedRevision,
      items: input.items,
      gold: input.gold,
      cash: input.cash,
      idempotencyKey: newIdempotencyKey('trade'),
    },
  });
}

/** This participant's own fee share. There is no field for the other's. */
export function setTradeFeeContribution(input: {
  tradeId: string;
  expectedRevision: number;
  gold: string;
  cash: string;
}): Promise<TradeResponse> {
  return request(
    tradeRoute(input.tradeId, '/fee-contribution'),
    (value) => tradeResponseSchema.parse(value),
    {
      method: 'PUT',
      body: {
        expectedRevision: input.expectedRevision,
        gold: input.gold,
        cash: input.cash,
        idempotencyKey: newIdempotencyKey('trade'),
      },
    },
  );
}

export function acceptTradeOffer(input: {
  tradeId: string;
  expectedRevision: number;
  acknowledgeEmptySide: boolean;
}): Promise<TradeResponse> {
  return request(
    tradeRoute(input.tradeId, '/accept-offer'),
    (value) => tradeResponseSchema.parse(value),
    {
      method: 'POST',
      body: {
        expectedRevision: input.expectedRevision,
        acknowledgeEmptySide: input.acknowledgeEmptySide,
        idempotencyKey: newIdempotencyKey('trade'),
      },
    },
  );
}

export function finalConfirmTrade(
  tradeId: string,
  expectedRevision: number,
): Promise<TradeResponse> {
  return request(
    tradeRoute(tradeId, '/final-confirm'),
    (value) => tradeResponseSchema.parse(value),
    {
      method: 'POST',
      body: { expectedRevision, idempotencyKey: newIdempotencyKey('trade') },
    },
  );
}

export function cancelTrade(tradeId: string): Promise<TradeResponse> {
  return request(tradeRoute(tradeId, '/cancel'), (value) => tradeResponseSchema.parse(value), {
    method: 'POST',
    body: { idempotencyKey: newIdempotencyKey('trade') },
  });
}

/* -------------------------------------------------------------------------- */
/*  Item marketplace                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The marketplace's whole API surface.
 *
 * No fee, no fee tier and no account id ever leaves this module in a request:
 * the seller and the buyer are the session, and the percentage is resolved
 * server-side at settlement. There is no client-side prediction here either -
 * a sale moves real items and real currency, so the panel draws what the server
 * answered.
 */

export function browseMarket(params: {
  cursor?: string;
  sort?: MarketSort;
  category?: string;
  rarity?: string;
  currency?: 'gold' | 'cash';
  minPrice?: string;
  maxPrice?: string;
  name?: string;
}): Promise<MarketBrowseResponse> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return request(`${MARKET_LISTINGS_ROUTE}${suffix}`, (value) =>
    marketBrowseResponseSchema.parse(value),
  );
}

/**
 * Cursor and drawer, for the two paged lists.
 *
 * The category goes to the SERVER rather than filtering what came back: a
 * client that hid rows would be paginating one list and showing another.
 */
function pageQuery(options: { cursor?: string; category?: MarketCategory }): string {
  const query = new URLSearchParams();
  if (options.cursor) {
    query.set('cursor', options.cursor);
  }
  // `all` is the absence of a filter, so it is never sent.
  if (options.category && options.category !== 'all') {
    query.set('category', options.category);
  }
  return query.size > 0 ? `?${query.toString()}` : '';
}

export function fetchMyListings(
  options: { cursor?: string; category?: MarketCategory } = {},
): Promise<MarketBrowseResponse> {
  return request(`${MARKET_MINE_ROUTE}${pageQuery(options)}`, (value) =>
    marketBrowseResponseSchema.parse(value),
  );
}

export function fetchMarketHistory(
  options: { cursor?: string; category?: MarketCategory } = {},
): Promise<MarketHistoryResponse> {
  return request(`${MARKET_HISTORY_ROUTE}${pageQuery(options)}`, (value) =>
    marketHistoryResponseSchema.parse(value),
  );
}

/** What a sale would cost at TODAY's rate. An estimate; the sale re-resolves it. */
export function fetchMarketQuote(
  price: string,
  currency: 'gold' | 'cash',
): Promise<MarketQuoteResponse> {
  return request(
    `${MARKET_QUOTE_ROUTE}?price=${encodeURIComponent(price)}&currency=${currency}`,
    (value) => marketQuoteResponseSchema.parse(value),
  );
}

export function createMarketListing(input: {
  instanceId: string;
  currency: 'gold' | 'cash';
  price: string;
}): Promise<MarketListingResponse> {
  return request(MARKET_LISTINGS_ROUTE, (value) => marketListingResponseSchema.parse(value), {
    method: 'POST',
    body: {
      kind: 'instance',
      instanceId: input.instanceId,
      currency: input.currency,
      price: input.price,
      idempotencyKey: newIdempotencyKey('market'),
    },
  });
}

export function cancelMarketListing(listingId: string): Promise<MarketListingResponse> {
  return request(
    marketListingRoute(listingId, '/cancel'),
    (value) => marketListingResponseSchema.parse(value),
    { method: 'POST', body: { idempotencyKey: newIdempotencyKey('market') } },
  );
}

/**
 * Buys a listing, stating the price the buyer was looking at.
 *
 * The server charges the LISTING's price; this only makes a stale screen a
 * refusal rather than a silent charge at a number nobody saw.
 */
export function buyMarketListing(input: {
  listingId: string;
  expectedPrice: string;
  expectedCurrency: 'gold' | 'cash';
}): Promise<MarketListingResponse> {
  return request(
    marketListingRoute(input.listingId, '/buy'),
    (value) => marketListingResponseSchema.parse(value),
    {
      method: 'POST',
      body: {
        expectedPrice: input.expectedPrice,
        expectedCurrency: input.expectedCurrency,
        idempotencyKey: newIdempotencyKey('market'),
      },
    },
  );
}

/* -------------------------------------------------------------------------- */
/*  Gacha                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The banner: prices, the FULL odds table, the pool and the caller's counter.
 *
 * One request, before anything is charged. The panel never computes a chance,
 * a price or a pity threshold of its own - it draws what this returned, so the
 * screen and the server can never disagree about what a pull costs or what it
 * can give.
 */
export function fetchGachaBanner(): Promise<GachaBannerResponse> {
  return request(GACHA_BANNER_ROUTE, (value) => gachaBannerResponseSchema.parse(value));
}

/**
 * One pull. The ONLY call that charges anything.
 *
 * Note what the body cannot say: no rarity, no item, no seed, no price and no
 * account. The server decides every one of those, and a replay of the same key
 * returns the original results without charging again.
 */
export function pullGacha(input: {
  bannerId: string;
  currency: 'gold' | 'cash';
  size: 1 | 10;
  idempotencyKey: string;
}): Promise<GachaPullResponse> {
  return request(GACHA_PULL_ROUTE, (value) => gachaPullResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

export function fetchGachaHistory(cursor?: string): Promise<GachaHistoryResponse> {
  const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return request(`${GACHA_HISTORY_ROUTE}${suffix}`, (value) =>
    gachaHistoryResponseSchema.parse(value),
  );
}

/* -------------------------------------------------------------------------- */
/*  Store                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The whole shop in one request: products, prices, allowance, debt and VIP.
 *
 * The panel never computes a price, a limit, a debt outcome or a VIP date of
 * its own — it draws what this returned. There is deliberately no second
 * catalogue in the frontend, so a screen and the charge it produces cannot
 * disagree about what something costs.
 */
export function fetchStoreCatalog(): Promise<StoreCatalogResponse> {
  return request(STORE_CATALOG_ROUTE, (value) => storeCatalogResponseSchema.parse(value));
}

/**
 * Creates an order and its Pix charge.
 *
 * The body says a `productId` and nothing else. The key travels in a header and
 * is the CALLER's to keep stable: retrying the same attempt with the same key
 * returns the same order rather than opening a second charge, which is the
 * whole reason it is a parameter here instead of being generated inside.
 */
export function createStoreOrder(input: {
  productId: string;
  idempotencyKey: string;
  /**
   * The price the player was LOOKING at, and the wording they ticked.
   *
   * Both are claims the server checks, never instructions it follows. Sending
   * them is what turns "the promotion ended while the confirmation was open"
   * from a silent overcharge into a `price_changed` refusal that costs nothing
   * - and what stops an acceptance of last month's terms standing in for this
   * month's.
   */
  expectedPriceCents?: number;
  acceptedTermsVersion?: string;
}): Promise<StoreOrderResponse> {
  return request(STORE_ORDERS_ROUTE, (value) => storeOrderResponseSchema.parse(value), {
    method: 'POST',
    body: {
      productId: input.productId,
      ...(input.expectedPriceCents === undefined
        ? {}
        : { expectedPriceCents: input.expectedPriceCents }),
      ...(input.acceptedTermsVersion === undefined
        ? {}
        : { acceptedTermsVersion: input.acceptedTermsVersion }),
    },
    headers: { [STORE_IDEMPOTENCY_HEADER]: input.idempotencyKey },
  });
}

/**
 * What the official launch would credit this account, as an estimate.
 *
 * Takes no argument, and the route takes no query: the account is the session.
 * The 30% is NEVER computed here - the numbers arrive already resolved, so a
 * screen cannot promise a bonus the server would not honour.
 */
export function fetchStoreRecreditPreview(): Promise<StoreRecreditPreviewResponse> {
  return request(STORE_RECREDIT_PREVIEW_ROUTE, (value) =>
    storeRecreditPreviewResponseSchema.parse(value),
  );
}

/** The caller's own orders, newest first. */
export function fetchStoreOrders(cursor?: string): Promise<StoreOrdersResponse> {
  const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return request(`${STORE_ORDERS_ROUTE}${suffix}`, (value) =>
    storeOrdersResponseSchema.parse(value),
  );
}

/**
 * One order, polled while it is pending.
 *
 * The ONLY way the browser learns a payment landed. It never talks to Mercado
 * Pago: the provider is reachable from the server alone, and a client that
 * could ask it directly could be lied to by anything on the network.
 */
export function fetchStoreOrder(orderId: string): Promise<StoreOrderResponse> {
  return request(storeOrderRoute(orderId), (value) => storeOrderResponseSchema.parse(value));
}

/* -------------------------------------------------------------------------- */
/*  The wardrobe (contract v3)                                                */
/* -------------------------------------------------------------------------- */

/**
 * The skin catalogue and every COPY this account holds.
 *
 * Two lists, and they must stay two lists all the way to the screen. The v2
 * shape answered one entry per definition with a single instance id, which
 * could not represent two copies at all - and after ADR 0040 two copies are the
 * normal case, because each is born with its own immutable socket capacity and
 * rolling twice is only meaningful when the second copy can be better.
 *
 * Parsed at the boundary like every other response here, so a server that
 * answered the old shape fails loudly rather than rendering half a wardrobe.
 */
export function fetchSkins(): Promise<SkinsResponse> {
  return request(SKINS_ROUTE, (value) => skinsResponseSchema.parse(value));
}

/**
 * Wears one COPY, named by its own instance id.
 *
 * Never a `definitionId`: two copies of one skin are two different things a
 * player can equip, and the one they picked is the one that must go on.
 */
export function equipSkin(input: {
  itemInstanceId: string;
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<SkinMutationResponse> {
  return request(SKIN_EQUIP_ROUTE, (value) => skinMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Takes the worn skin off. There is one skin slot, so nothing names it. */
export function unequipSkin(input: {
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<SkinMutationResponse> {
  return request(SKIN_UNEQUIP_ROUTE, (value) => skinMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/* -------------------------------------------------------------------------- */
/*  The Cash skin roulette (contract v2)                                      */
/* -------------------------------------------------------------------------- */

/**
 * The offer, its full odds table and the caller's balance, in one request.
 *
 * Everything a player has to read before spending arrives here: the price, the
 * currency, the pool with a chance per skin, all six socket chances, the
 * duplicate policy, the fact that the copy is bound, the fact that sockets do
 * nothing in a duel, the absence of a guarantee and which release stage's reset
 * rules apply. The panel computes none of it.
 *
 * A 404 with `banner_not_available` is the ANSWER in every shipped build - no
 * banner is active - and the caller renders "indisponível" rather than treating
 * it as an outage.
 */
export function fetchSkinGachaBanner(): Promise<SkinGachaBannerResponse> {
  return request(SKIN_GACHA_BANNER_ROUTE, (value) => skinGachaBannerResponseSchema.parse(value));
}

/**
 * One roll. The only call in the product that creates a skin, and it charges.
 *
 * The key is minted by the CALLER and reused for as long as one intention is
 * being retried, which is what makes an uncertain answer safe to repeat: the
 * same key returns the same copy with the same capacity and one charge. A
 * deliberately new purchase is a new key, and the same key naming a different
 * banner is a conflict rather than a second roll.
 *
 * Nothing in this body can influence the outcome. There is no definition, no
 * socket count, no price and no seed - the request may only say which offer it
 * believes it is taking.
 */
export function rollSkinGacha(input: {
  bannerId: string;
  bannerVersion: number;
  bannerFingerprint: string;
  idempotencyKey: string;
}): Promise<SkinGachaRollResponse> {
  return request(SKIN_GACHA_ROLL_ROUTE, (value) => skinGachaRollResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/* -------------------------------------------------------------------------- */
/*  Joias (gate V28C)                                                         */
/* -------------------------------------------------------------------------- */

/**
 * As joias desta conta, e onde cada peça está.
 *
 * Uma leitura só, e é a mesma forma que qualquer mutação devolve: o cliente não
 * aplica nada de forma otimista, envia a intenção e desenha o que voltar. Um
 * socket pintado cheio antes da resposta é um socket errado quando a resposta é
 * uma recusa.
 */
export function fetchJewels(): Promise<JewelsResponse> {
  return request(JEWELS_ROUTE, (value) => jewelsResponseSchema.parse(value));
}

/**
 * Encaixa uma joia num assento vazio.
 *
 * O assento é `(skinInstanceId, socketIndex)` e a peça é `jewelInstanceId` -
 * identidades persistidas, nunca posições numa lista. A interface pode reordenar
 * o que quiser; o assento 3 continua a ser o assento 3.
 */
export function insertJewel(input: {
  skinInstanceId: string;
  socketIndex: number;
  jewelInstanceId: string;
  idempotencyKey: string;
}): Promise<JewelMutationResponse> {
  return request(JEWEL_SOCKET_INSERT_ROUTE, (value) => jewelMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Retira a joia de um assento. Devolve-a intacta ao inventário. */
export function removeJewel(input: {
  skinInstanceId: string;
  socketIndex: number;
  idempotencyKey: string;
}): Promise<JewelMutationResponse> {
  return request(JEWEL_SOCKET_REMOVE_ROUTE, (value) => jewelMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * Troca a joia de um assento por outra, numa operação só.
 *
 * Não é retirar e depois encaixar: entre as duas chamadas existiria um instante
 * em que o assento está vazio e a peça antiga solta, e um erro a meio deixaria o
 * jogador exatamente aí.
 */
export function swapJewel(input: {
  skinInstanceId: string;
  socketIndex: number;
  jewelInstanceId: string;
  idempotencyKey: string;
}): Promise<JewelMutationResponse> {
  return request(JEWEL_SOCKET_SWAP_ROUTE, (value) => jewelMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * Incorpora um Coração Primordial numa nave (gate V28F.C).
 *
 * A única das cinco que é IRREVERSÍVEL: o coração é gasto e não volta ao
 * inventário. Não há função inversa neste ficheiro porque não há rota inversa no
 * servidor - desincorporar seria uma decisão de produto que ninguém tomou.
 *
 * Devolve a view INTEIRA, como as outras: a contagem nova vem lá dentro em
 * `ships`, e quem chama não soma nada.
 */
export function incorporatePrimordialHeart(input: {
  shipInstanceId: string;
  jewelInstanceId: string;
  idempotencyKey: string;
}): Promise<JewelMutationResponse> {
  return request(
    JEWEL_PRIMORDIAL_INCORPORATE_ROUTE,
    (value) => jewelMutationResponseSchema.parse(value),
    { method: 'POST', body: input },
  );
}

/* -------------------------------------------------------------------------- */
/*  Cartas (gate V28H.G)                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Os três espaços de carta, como o servidor os tem.
 *
 * Sempre três entradas, mesmo vazias: a interface desenha três caixas, e uma
 * lista que encolhesse obrigá-la-ia a adivinhar quais faltam.
 *
 * A COLEÇÃO não vem daqui - ela é a lista de instâncias de `/api/inventory`
 * filtrada por categoria. Uma segunda rota que devolvesse as mesmas linhas seria
 * a segunda fonte de verdade sobre o que a conta tem.
 */
export function fetchCards(): Promise<CardsListResponse> {
  return request(CARDS_ROUTE, (value) => cardsListResponseSchema.parse(value));
}

/**
 * Põe uma carta num dos três espaços.
 *
 * O pedido nomeia o espaço, a instância, a versão que o cliente julga ter e uma
 * chave. Nunca a definição, o nível ou a categoria: os três são relidos sob os
 * bloqueios do servidor.
 */
export function equipCard(input: {
  slot: CardSlot;
  itemInstanceId: string;
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<CardMutationResponse> {
  return request(CARD_EQUIP_ROUTE, (value) => cardMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/** Esvazia um dos três espaços. */
export function unequipCard(input: {
  slot: CardSlot;
  expectedLoadoutVersion: number;
  idempotencyKey: string;
}): Promise<CardMutationResponse> {
  return request(CARD_UNEQUIP_ROUTE, (value) => cardMutationResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}

/**
 * Funde duas cartas iguais numa do nível seguinte.
 *
 * MANUAL, e vai continuar a ser: nada funde duplicados por conta do jogador.
 * Uma segunda cópia é uma coisa que ele pode querer guardar para outro espaço.
 */
export function combineCards(input: {
  firstInstanceId: string;
  secondInstanceId: string;
  idempotencyKey: string;
}): Promise<CardCombineResponse> {
  return request(CARD_COMBINE_ROUTE, (value) => cardCombineResponseSchema.parse(value), {
    method: 'POST',
    body: input,
  });
}
